import { observer } from "mobx-react-lite";

import { getSymbolFontContext } from "@/components/expression/utils/getSymbolFontContext";
import * as Node from "@/components/expression/utils/layout";
import {
  NodeAttachments,
  type NodeAttachment,
} from "@/components/expression/utils/NodeAttachments";
import { useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { isLabelChild } from "@/models/nodes/utils";
import { twMerge } from "@/utils/twMerge";

import { useSymbolPushTailwindClassName } from "./utils/layout/useSymbolPushTailwindClassName";

import type { ExpressionComponent } from "./types";
import type { NodeSize } from "@corca/data";
import type { ComponentProps } from "react";

export const SymbolNode: ExpressionComponent<"symbol", ComponentProps<"div">> = observer(
  function SymbolNode({ node, className, nodeRenderProps: _, ...rest }) {
    const { value, labelNodes, parent } = node;

    const { size, view, placeholderVisible } = useNodeStyle();
    const { fontSize, fontFeature } = node.getFont(getSymbolFontContext(view, size));
    const isTextView = view === "text";
    const nextSize: NodeSize = size === "sm" ? "xxs" : "xxxs";
    const textColor = parent && "color" in parent ? parent.color : undefined;

    const attachments: NodeAttachment[] = labelNodes.map((labelNode) => ({
      node: labelNode.inlineNode,
      position: labelNode.position,
    }));
    const parentIsFunction = parent?.is(FunctionNodeModel);
    const addLeftLabelsPadding = !isTextView && node.hasLabels("left");
    const addRightLabelsPadding =
      !isTextView && (node.hasLabels("right") || placeholderVisible !== "none");
    const addFunctionMinHeight = parentIsFunction && node.isLargeOperator && nextSize === "xxs";
    const symbolPushClassName = useSymbolPushTailwindClassName(value);

    let nodeFontSize = fontSize;
    if (parentIsFunction && view === "compact-expression") {
      nodeFontSize = "16";
      // TODO: this clamp shrinks a display glyph instead of selecting the inline one.
      // See the TODO in `useSymbolFontContext`.
    } else if (fontSize && isLabelChild(node) && parseInt(fontSize, 10) > 18) {
      nodeFontSize = "18";
    }

    return (
      <div
        className={twMerge(
          "flex flex-row items-center transition-spacing",
          "before:content-zero-space",
          className,
        )}
        {...rest}
      >
        <NodeAttachments
          attachments={attachments}
          position="left"
          className={twMerge(
            nodeFontSize && "self-stretch",
            addLeftLabelsPadding && "pr-0.5",
            addFunctionMinHeight && "min-h-9",
            node.isLargeOperator && "py-1",
          )}
          styleProviderValue={{
            view: "text",
            size: nextSize,
            text: parentIsFunction ? "parameter" : "default",
          }}
        />
        <Node.Value
          node={parent}
          color={textColor}
          isHighlighted={node.isHighlighted}
          className={twMerge(
            "relative",
            nodeFontSize && `text-node-size-${nodeFontSize}`,
            fontFeature && `font-${fontFeature}`,
          )}
          highlightClassName={symbolPushClassName?.positive}
        >
          {value}
        </Node.Value>
        <NodeAttachments
          attachments={attachments}
          position="right"
          className={twMerge(
            nodeFontSize && "self-stretch",
            addRightLabelsPadding && "pl-0.5",
            addFunctionMinHeight && "min-h-9",
            node.isLargeOperator && "py-1",
          )}
          styleProviderValue={{
            view: "text",
            size: nextSize,
            text: parentIsFunction ? "parameter" : "default",
          }}
        />
      </div>
    );
  },
);
